import React, { useRef } from 'react';
import { Scatter } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { enUS } from 'date-fns/locale';
import moment from 'moment';
import { Vital } from '../api/fetchPastVitalsByPtOid';
import { ActiveElement, Chart, ChartEvent } from 'chart.js';

const graphOptions = (unit?: string) => {
    return {
        font: {
            family: 'Lato',
        },
        animations: {
            radius: {
                duration: 400,
                easing: 'linear',
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function () {
                        return '';
                    },
                    footer: function (context: any) {
                        if (!context[0]?.label) {
                            return context[0].label;
                        }

                        const label = context[0].raw.v.isoDateObject;
                        // parse from the format 'Apr 2, 2024, 2:41:27 a.m'
                        const date = moment(label).format('MMM D, YYYY');

                        const dateString = date;

                        if (context[0].datasetIndex === 0) {
                            return `${dateString}`;
                        }
                        return dateString;
                    },
                    title: function (context: any) {
                        return `${context[0].parsed.y}${unit ? ` ${unit}` : ''}`;
                    },
                },
                titleFont: {
                    size: 13,
                    weight: '700',
                },
                footerFont: {
                    size: 11,
                    weight: '400',
                },
            },
            datalabels: {
                // display: false,
                formatter: function () {
                    return '';
                },
                align: 'top',
            },
        },
        onHover: (event: ChartEvent, elements: ActiveElement[]) => {
            if (event.native && 'target' in event.native) {
                const chartElement = event.native.target as HTMLElement;
                if (chartElement) {
                    chartElement.style.cursor = elements.length ? 'pointer' : 'default';
                }
            }
        },
        scales: {
            x: {
                offset: true,
                ticks: {
                    source: 'data',
                    beginAtZero: true,
                },
                grid: {
                    display: false,
                },
                type: 'category',
                adapters: {
                    date: {
                        locale: enUS,
                    },
                },
                time: {
                    unit: 'year',
                },
            },
            y: {
                offset: true,
                grid: {
                    display: false,
                },
                title: {
                    text: 'Result',
                    color: '#858C94',
                    font: {
                        size: 14,
                        weight: '700',
                    },
                    display: true,
                },
            },
        },
        ticks: {
            padding: 8,
            color: '#09101D',
            font: {
                weight: '700',
            },
            precision: 0,
        },
    };
};

type GraphData = {
    x: string;
    y: number;
    isOutOfRange?: boolean;
    v?: Vital;
}[];

const VitalsGraph: React.FC<{
    graphData: GraphData;
    unit?: string;
    handleValueClick: (vital?: Vital) => void;
}> = ({ graphData, unit, handleValueClick }) => {
    const chartRef = useRef<Chart>(null);
    const options = graphOptions(unit);

    const handlePointClick = (event: Event) => {
        const chart = chartRef.current;
        if (!chart) return;
        const elements = chart.getElementsAtEventForMode(
            event,
            'nearest',
            { intersect: true },
            false,
        );

        if (elements.length > 0) {
            const elementIndex = elements[0].index;
            const clickedPoint = graphData[elementIndex];
            console.log('Clicked Point:', clickedPoint);
            handleValueClick(clickedPoint.v);
        } else {
            console.log('No point clicked');
        }
    };

    return (
        <div className="trend-graph p-8 pt-12 bg-white mx-24 my-18 rounded-8">
            <Scatter
                ref={chartRef}
                data={{
                    datasets: [
                        {
                            label: 'Vital Trend',
                            data: graphData,
                            backgroundColor: '#215FFF',
                            borderColor: '#215FFF',
                            pointRadius: 4,
                            pointBackgroundColor: graphData.map((point) =>
                                point.isOutOfRange ? '#D74B41' : '#215FFF',
                            ),
                            pointBorderColor: graphData.map((point) =>
                                point.isOutOfRange ? '#D74B41' : '#215FFF',
                            ),
                        },
                    ],
                }}
                options={{ ...options, onClick: handlePointClick }}
                className="h-160 max-h-160"
                onPointerLeaveCapture={undefined}
            />
            <p className="text-14 font-700 text-center text-neutral-5">Date of Record</p>
        </div>
    );
};

export default React.memo(VitalsGraph, (prevProps, nextProps) => {
    return prevProps.graphData.length === nextProps.graphData.length;
});
